import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, MessageCircle, CheckCircle, XCircle, Volume2, Send } from 'lucide-react';
import { apiCall, speak, randomEncouragement } from './api';
import { toast } from 'sonner';

/**
 * Scénarios revus en style chat WhatsApp : bulles progressives, options réponses,
 * feedback temps-réel.
 */
export default function Scenarios({ token, onBack, onUserUpdate }) {
  const [list, setList]     = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { apiCall('/scenarios').then(setList).catch(()=>{}); }, []);

  if (active) return <ChatScenarioPlayer scenarioId={active} token={token} onBack={() => setActive(null)} onUserUpdate={onUserUpdate} />;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto px-4 py-8" data-testid="scenarios-list">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-4 flex items-center gap-1"><ChevronLeft size={16} />Retour</button>
      <h2 className="text-2xl font-bold mb-2">Scénarios interactifs</h2>
      <p className="text-white/60 text-sm mb-6">Discute en mode chat. Les messages apparaissent comme dans une vraie conversation.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map(s => (
          <motion.button key={s.id} whileHover={{ y: -2 }} onClick={() => setActive(s.id)} data-testid={`scenario-${s.id}`}
            className="text-left bg-gradient-to-br from-emerald-700/30 to-slate-900 border border-emerald-500/20 hover:border-emerald-500/50 rounded-2xl p-5 transition">
            <div className="text-4xl mb-3">{s.icon}</div>
            <div className="text-xs text-emerald-300 font-bold uppercase tracking-wider">{s.level} · {s.steps_count} étapes</div>
            <div className="font-bold text-lg mt-1">{s.title}</div>
            <div className="text-xs text-white/60 mt-2">{s.desc}</div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
}

function ChatScenarioPlayer({ scenarioId, token, onBack, onUserUpdate }) {
  const [scenario, setScenario] = useState(null);
  const [idx, setIdx]           = useState(0);
  const [messages, setMessages] = useState([]); // [{ from: 'npc'|'user'|'feedback', text, ok? }]
  const [waiting, setWaiting]   = useState(false);
  const [chosen, setChosen]     = useState(null);
  const [score, setScore]       = useState(0);
  const [done, setDone]         = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { apiCall(`/scenarios/${scenarioId}`).then(setScenario).catch(() => onBack()); }, [scenarioId]);

  // When scenario or step changes → inject NPC bubble (simulating typing delay)
  useEffect(() => {
    if (!scenario) return;
    const cur = scenario.steps[idx];
    if (!cur) return;
    setWaiting(true);
    const t1 = setTimeout(() => {
      setMessages(m => [...m, { from: 'npc', text: cur.npc }]);
      speak(cur.npc, 'en-US', 0.9);
      setWaiting(false);
    }, 900);
    return () => clearTimeout(t1);
  }, [scenario, idx]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, waiting]);

  if (!scenario) return <div className="max-w-2xl mx-auto px-4 py-12 text-center text-white/60">Chargement...</div>;
  const current = scenario.steps[idx];

  const choose = (i) => {
    if (chosen !== null) return;
    setChosen(i);
    const opt = current.options[i];
    setMessages(m => [...m, { from: 'user', text: opt.text }]);
    setTimeout(() => {
      setMessages(m => [...m, { from: 'feedback', text: opt.feedback, ok: opt.ok }]);
      if (opt.ok) setScore(s => s + 1);
    }, 600);
  };

  const next = async () => {
    setChosen(null);
    if (idx < scenario.steps.length - 1) {
      setIdx(idx + 1);
    } else {
      const finalScore = Math.round((score / scenario.steps.length) * 100);
      try {
        const r = await apiCall('/scenarios/complete', 'POST', { scenario_id: scenarioId, score: finalScore }, token);
        onUserUpdate?.(prev => prev ? { ...prev, xp: r.total_xp } : prev);
        toast.success(`Scénario terminé ! +${r.xp_gained} XP`);
      } catch {}
      setDone(true);
    }
  };

  if (done) {
    const finalPct = Math.round(score / scenario.steps.length * 100);
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-xl mx-auto px-4 py-12 text-center" data-testid="scenario-done">
        <div className="text-6xl mb-3">{finalPct >= 75 ? '🏆' : finalPct >= 50 ? '👍' : '💪'}</div>
        <h3 className="text-2xl font-bold">Score : {finalPct}%</h3>
        <p className="text-white/60 mt-2">{randomEncouragement()}</p>
        <button onClick={onBack} className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold px-6 py-3 rounded-xl" data-testid="scenario-back">Retour aux scénarios</button>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto px-4 py-6" data-testid="scenario-chat">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-4 flex items-center gap-1"><ChevronLeft size={16} />Quitter</button>

      {/* Chat header (style WhatsApp) */}
      <div className="bg-emerald-700 rounded-t-2xl p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-300 flex items-center justify-center text-2xl">{scenario.icon}</div>
        <div className="flex-1">
          <div className="font-bold text-sm">{scenario.title}</div>
          <div className="text-[11px] text-emerald-200">{waiting ? 'en train d\'écrire…' : 'en ligne'}</div>
        </div>
        <div className="flex gap-1">
          {scenario.steps.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${i <= idx ? 'bg-yellow-300' : 'bg-white/20'}`} />)}
        </div>
      </div>

      {/* Chat body */}
      <div ref={scrollRef} className="bg-slate-900/70 backdrop-blur p-4 space-y-2 h-96 overflow-y-auto" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.02) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
        <AnimatePresence>
          {messages.map((m, i) => (
            <motion.div key={i}
              initial={{ y: 12, opacity: 0, scale: 0.9 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.from === 'feedback' ? (
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-xs ${m.ok ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30' : 'bg-red-500/20 text-red-200 border border-red-500/30'}`}>
                  {m.ok ? <CheckCircle size={12} className="inline mr-1" /> : <XCircle size={12} className="inline mr-1" />}{m.text}
                </div>
              ) : m.from === 'npc' ? (
                <div className="max-w-[80%] bg-slate-700 text-white rounded-2xl rounded-tl-sm px-3 py-2 text-sm flex items-end gap-2">
                  <span>{m.text}</span>
                  <button onClick={() => speak(m.text, 'en-US', 0.9)} className="text-white/40 hover:text-white"><Volume2 size={14} /></button>
                </div>
              ) : (
                <div className="max-w-[80%] bg-emerald-500 text-slate-950 font-bold rounded-2xl rounded-tr-sm px-3 py-2 text-sm">
                  {m.text}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        {waiting && (
          <div className="flex justify-start">
            <div className="bg-slate-700 rounded-2xl rounded-tl-sm px-3 py-2.5 flex gap-1">
              <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" />
              <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
              <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
            </div>
          </div>
        )}
      </div>

      {/* Input area = answer options */}
      <div className="bg-slate-800 rounded-b-2xl p-3 space-y-2">
        {chosen === null && !waiting && current && (
          <div className="space-y-1.5">
            <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Choisis ta réponse</div>
            {current.options.map((o, i) => (
              <button key={i} onClick={() => choose(i)} data-testid={`scenario-opt-${i}`}
                className="w-full text-left p-2.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-sm flex items-center justify-between gap-2 transition">
                <span>{o.text}</span>
                <Send size={12} className="text-emerald-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
        {chosen !== null && (
          <button onClick={next} className="w-full bg-yellow-500 hover:bg-yellow-400 text-slate-950 font-bold py-2.5 rounded-xl" data-testid="scenario-next">
            {idx < scenario.steps.length - 1 ? 'Continuer la conversation' : 'Voir le score final'}
          </button>
        )}
      </div>
    </motion.div>
  );
}
